import { useEffect, useState } from 'react'
import { Box, Typography, CircularProgress, Alert, Link as MuiLink, Button } from '@mui/material'
import { Link, useSearchParams } from 'react-router-dom'
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline'
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline'
import { type APIResponse, DASHBOARD_AUTH_PATHS } from '@yourownmetric/shared'

import { apiSlice } from '@/apis/apiSlice'

const verifyEmailApi = apiSlice.injectEndpoints({
    endpoints: builder => ({
        verifyEmail: builder.mutation<{ message: string }, { token: string }>({
            query: body => ({
                url: '/auth/verify-email',
                method: 'POST',
                body,
            }),
            transformResponse: (res: APIResponse<{ message: string }>) => res.data!,
        }),
    }),
})

const { useVerifyEmailMutation } = verifyEmailApi

export function VerifyEmailPage() {
    const [searchParams] = useSearchParams()
    const token = searchParams.get('token')
    const [verifyEmail, { isLoading }] = useVerifyEmailMutation()
    const [status, setStatus] = useState<'loading' | 'success' | 'error' | 'no-token'>('loading')
    const [errorMessage, setErrorMessage] = useState('')

    useEffect(() => {
        if (!token) {
            setStatus('no-token')
            return
        }

        verifyEmail({ token })
            .unwrap()
            .then(() => setStatus('success'))
            .catch(err => {
                setStatus('error')
                const errorCode = (err as { data?: { error?: { code?: string } } })?.data?.error
                    ?.code
                if (errorCode === 'TOKEN_EXPIRED') {
                    setErrorMessage(
                        'This verification link has expired. Please request a new one from your dashboard.',
                    )
                } else if (errorCode === 'INVALID_TOKEN') {
                    setErrorMessage('This verification link is invalid or has already been used.')
                } else {
                    setErrorMessage('Something went wrong. Please try again later.')
                }
            })
    }, [token, verifyEmail])

    if (status === 'loading' || isLoading) {
        return (
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
                <CircularProgress size={48} />
                <Typography variant="h5">Verifying your email…</Typography>
                <Typography variant="body2" color="text.secondary">
                    This will only take a moment.
                </Typography>
            </Box>
        )
    }

    if (status === 'no-token') {
        return (
            <Box
                sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    gap: 2,
                    textAlign: 'center',
                }}
            >
                <ErrorOutlineIcon sx={{ fontSize: 56, color: 'warning.main' }} />
                <Typography variant="h5">Missing verification token</Typography>
                <Typography variant="body2" color="text.secondary">
                    This link appears to be incomplete. Check your email for the full verification
                    link.
                </Typography>
                <MuiLink component={Link} to={DASHBOARD_AUTH_PATHS.login} variant="body2">
                    Go to Sign In
                </MuiLink>
            </Box>
        )
    }

    if (status === 'success') {
        return (
            <Box
                sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    gap: 2,
                    textAlign: 'center',
                }}
            >
                <CheckCircleOutlineIcon sx={{ fontSize: 56, color: 'success.main' }} />
                <Typography variant="h5">Email verified!</Typography>
                <Typography variant="body2" color="text.secondary">
                    Your email has been verified successfully. You can now sign in to your account.
                </Typography>
                <Button
                    component={Link}
                    to={DASHBOARD_AUTH_PATHS.login}
                    variant="contained"
                    size="large"
                    sx={{ mt: 1 }}
                >
                    Sign In
                </Button>
            </Box>
        )
    }

    // error state
    return (
        <Box
            sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: 2,
                textAlign: 'center',
            }}
        >
            <ErrorOutlineIcon sx={{ fontSize: 56, color: 'error.main' }} />
            <Typography variant="h5">Verification failed</Typography>
            <Alert severity="error" sx={{ width: '100%' }}>
                {errorMessage}
            </Alert>
            <MuiLink component={Link} to={DASHBOARD_AUTH_PATHS.login} variant="body2">
                Go to Sign In
            </MuiLink>
        </Box>
    )
}
